"use client";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { FeatureItem } from "@components/atoms/FeatureItem";
import { createVariantSpecificationsFromApi } from "@factories/variantSpecificationsFactory";
import { fetchVariantSpecifications } from "@services/variantSpecificationsService";
import type { VariantComparisonResponse } from "@services/variantSpecificationsService";
import { fetchModelDetailsVariants } from "@services/queries/findModelDetailsVariants";
import { cn } from "@utils/utils";
import { CategoryTabs } from "./CategoryTabs";
import styles from "./SpecificationSection.module.scss";
import { VariantSelector } from "./VariantSelector";
import { Loading } from "./Loading";
import ChatbotSupport from "@components/molecules/ChatbotSupport";
import { DEFAULT_CITY } from "@constants/global";

interface AllVariantSlugs {
  uniqueSortedVariants?: {
    variantName: string;
    slug: string;
  }[];
}

interface DetailedVariant {
  variantName: string;
  fuelType: string;
  engine: string;
  emission: string;
  transmission: string;
  slug: string;
  marketPrice: number;
  adspPrice: number;
  ackoDriveSavings: number;
  exShowRoomPrice: number;
  onRoadPrice: number;
  mileage: {
    value: number;
    unit: string;
  };
  variantLevel: string;
  expressDelivery: boolean;
  expressDeliveryDays: number;
  standardDeliveryDays: number;
  status: string;
}

interface SpecificationsSectionClientWrapperProps {
  variantSlug: string;
  allVariantSlugs: AllVariantSlugs;
  selectedVariantId?: string;
  selectedCategoryIndex?: number;
  className?: string;
  carName?: string;
  showVariantDropdown?: boolean;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  isSellable?: boolean;
  pageType?: string;
  carAvailability?: string;
  isLoggedIn?: boolean;
  cityName?: string;
  currentVariantSlug: string;
  allAvailableVariants: any[];
  selectedVariant: any;
  isUpsellFlow?: boolean;
  initialApiResponse?: VariantComparisonResponse | null;
  initialDetailedVariants?: DetailedVariant[];
}

export default function SpecificationsSectionClientWrapper({
  variantSlug,
  selectedCategoryIndex = 0,
  className,
  carName,
  showVariantDropdown = true,
  brandName,
  modelName,
  variantName,
  pageType,
  isSellable,
  carAvailability,
  isLoggedIn,
  cityName,
  currentVariantSlug,
  allAvailableVariants,
  selectedVariant,
  isUpsellFlow,
  initialApiResponse = null,
  initialDetailedVariants = [],
}: SpecificationsSectionClientWrapperProps) {
  const searchParams = useSearchParams();
  const [apiResponse, setApiResponse] = useState<VariantComparisonResponse | null>(
    initialApiResponse
  );
  const [loading, setLoading] = useState<boolean>(!initialApiResponse);
  const [detailedVariants, setDetailedVariants] = useState<DetailedVariant[]>(
    initialDetailedVariants
  );
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(
    selectedCategoryIndex
  );
  const categoryParam = searchParams?.get("category");

  useEffect(() => {
    const parsedCategoryIndex =
      categoryParam !== null && categoryParam !== undefined
        ? Number(categoryParam)
        : selectedCategoryIndex;

    if (Number.isFinite(parsedCategoryIndex) && parsedCategoryIndex >= 0) {
      setActiveCategoryIndex(parsedCategoryIndex);
    }
  }, [categoryParam, selectedCategoryIndex]);

  useEffect(() => {
    if (initialApiResponse) {
      setApiResponse(initialApiResponse);
      setDetailedVariants(initialDetailedVariants);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const [specificationsResponse, detailedVariantsResponse] =
          await Promise.all([
            fetchVariantSpecifications(currentVariantSlug),
            fetchModelDetailsVariants(variantSlug, cityName || DEFAULT_CITY),
          ]);

        if (isMounted) {
          setApiResponse(specificationsResponse);
          setDetailedVariants(
            detailedVariantsResponse?.allVariantSlugs?.uniqueSortedVariants ||
              []
          );
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setApiResponse(null);
          setDetailedVariants([]);
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    currentVariantSlug,
    variantSlug,
    cityName,
    initialApiResponse,
    initialDetailedVariants,
  ]);

  if (loading) {
    return (
      <div className={cn(styles.specs, className)}>
        <Loading />
      </div>
    );
  }

  if (!apiResponse) {
    return (
      <div className={cn(styles.specs, className)}>
        <div className={styles.specs__error}>Failed to load specifications</div>
      </div>
    );
  }

  const specificationsVariants =
    createVariantSpecificationsFromApi(apiResponse);

  if (allAvailableVariants.length === 0) {
    return (
      <div className={cn(styles.specs, className)}>
        <div className={styles.specs__empty}>
          No variants available for this model.
        </div>
      </div>
    );
  }

  const currentVariant =
    specificationsVariants.length > 0 ? specificationsVariants[0] : null;

  if (
    !currentVariant ||
    (currentVariant && currentVariant.categories.length === 0)
  ) {
    return (
      <div className={cn(styles.specs, className)}>
        <div className={styles.specs__empty}>
          No specifications available for this variant.
        </div>
      </div>
    );
  }

  const currentCategories = currentVariant.categories || [];
  const categoryIndex =
    Number.isFinite(activeCategoryIndex) && activeCategoryIndex >= 0
      ? Math.min(activeCategoryIndex, currentCategories.length - 1)
      : 0;

  return (
    <>
      {showVariantDropdown && (
        <VariantSelector
          variants={allAvailableVariants}
          selectedVariantId={selectedVariant.id}
          detailedVariants={detailedVariants}
          brandName={brandName}
          modelName={modelName}
          variantName={variantName}
          isSellable={isSellable}
          pageType={pageType}
          carAvailability={carAvailability}
          isLoggedIn={isLoggedIn}
          cityName={cityName}
        />
      )}
      <div className={styles.specs__contentContainer}>
        <div className={styles.specs__content}>
          <CategoryTabs
            categories={currentCategories}
            selectedCategoryIndex={categoryIndex}
            selectedVariantId={selectedVariant.id}
            brandName={brandName}
            modelName={modelName}
            variantName={variantName}
            pageType={pageType}
            carAvailability={carAvailability}
            onCategoryChange={setActiveCategoryIndex}
          />

          <div className={styles.specs__tabContent}>
            {currentCategories.map((category, index) => {
              const includedFeatures = category.features.filter(
                (feature) => feature.included
              );
              const missingFeatures = category.features.filter(
                (feature) => !feature.included
              );
              const isActive = index === categoryIndex;

              return (
                <div
                  key={category.title}
                  hidden={!isActive}
                  aria-hidden={!isActive}
                  data-category-index={index}
                  className={styles.specs__features}
                >
                  {includedFeatures.length > 0 && (
                    <div className={styles.specs__featureColumn}>
                      <h3 className={styles.specs__featureTitle}>
                        What&apos;s included
                      </h3>
                      <div className={styles.specs__featureList}>
                        {includedFeatures.map((feature, featureIndex) => (
                          <FeatureItem
                            key={`included-${category.title}-${feature.title}-${featureIndex}`}
                            {...feature}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {missingFeatures.length > 0 && (
                    <div className={styles.specs__featureColumn}>
                      <h3 className={styles.specs__featureTitle}>
                        Features you&apos;ll miss
                      </h3>
                      <div className={styles.specs__featureList}>
                        {missingFeatures.map((feature, featureIndex) => (
                          <FeatureItem
                            key={`missing-${category.title}-${feature.title}-${featureIndex}`}
                            {...feature}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {
          !isUpsellFlow &&
          <div className={styles.specs__chatbotContainer}>
            <ChatbotSupport
              heading={"Not sure which variant suits you?"}
              subheading="No worries, we'll help you with your question"
              buttonText={"Find the right variant"}
              buttonLink={""}
              icon={`/icons/chatbot.svg`}
              customChatbotStyling={styles.customChatbotStyling}
              chatBotPrompt={`Which variant should I buy in ${carName}?`}
              brandName={brandName}
              modelName={modelName}
              variantName={variantName}
              carAvailability={carAvailability}
              pageType={pageType}
              section="Right variant"
              cityName={cityName}
            />
          </div>
        }
      </div>
    </>
  );
}
